import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminSession } from './adminSession';
import { AdminOrderNotificationItem, fetchAdminOrderNotifications } from './adminService';
import './AdminOrderAlert.scss';

// 신규 주문 알림: 관리자 페이지마다, 그리고 홈(사용자 화면)에서도 새로 마운트되므로 기준 시각과 알림 목록을 sessionStorage에 이어서 보관한다
const NOTIFY_SINCE_KEY = 'adminNotifySince';
const NOTIFY_QUEUE_KEY = 'adminNotifyQueue';
const NOTIFY_POLL_MS = 30 * 1000;
const NOTIFY_MAX = 5;

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage를 쓸 수 없으면 이번 화면에서만 유지된다
  }
}

// 30초마다 새로 들어온 주문/견적문의를 확인한다 (token이 없으면 조회하지 않음)
export function useAdminOrderNotifications(token: string | undefined) {
  const [pending, setPending] = useState<{ inquiries: number; orders: number } | null>(null);
  const [alerts, setAlerts] = useState<AdminOrderNotificationItem[]>(() => readStored(NOTIFY_QUEUE_KEY, []));

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const poll = () => {
      const since = readStored<string | undefined>(NOTIFY_SINCE_KEY, undefined);
      fetchAdminOrderNotifications(token, since)
        .then((result) => {
          if (cancelled) return;
          writeStored(NOTIFY_SINCE_KEY, result.now);
          setPending({ inquiries: result.pendingInquiries, orders: result.pendingOrders });
          if (result.orders.length > 0) {
            setAlerts((prev) => {
              const known = new Set(prev.map((a) => a.id));
              const next = [...result.orders.filter((o) => !known.has(o.id)), ...prev].slice(0, NOTIFY_MAX);
              writeStored(NOTIFY_QUEUE_KEY, next);
              return next;
            });
          }
        })
        .catch(() => {
          // 알림 조회 실패는 화면 이용을 막지 않는다
        });
    };

    poll();
    const timer = window.setInterval(poll, NOTIFY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [token]);

  const dismissAlert = (id?: string) => {
    setAlerts((prev) => {
      const next = id ? prev.filter((a) => a.id !== id) : [];
      writeStored(NOTIFY_QUEUE_KEY, next);
      return next;
    });
  };

  return { pending, alerts, dismissAlert };
}

interface AdminOrderAlertPopupProps {
  alerts: AdminOrderNotificationItem[];
  onDismiss: (id?: string) => void;
  className?: string;
}

// 신규 주문 알림 팝업
export function AdminOrderAlertPopup({ alerts, onDismiss, className = '' }: AdminOrderAlertPopupProps) {
  if (alerts.length === 0) return null;

  return (
    <div className={`order-alert ${className}`} role="alert" aria-live="assertive">
      <div className="order-alert-head">
        <strong>신규 접수 {alerts.length}건</strong>
        <button type="button" onClick={() => onDismiss()} aria-label="알림 모두 닫기">
          모두 확인
        </button>
      </div>
      <ul>
        {alerts.map((a) => (
          <li key={a.id}>
            <Link to={`/admin/orders/${a.id}`} onClick={() => onDismiss(a.id)} className="order-alert-item">
              <span className={`order-alert-type ${a.request_type === 'ORDER' ? 'is-order' : ''}`}>
                {a.request_type === 'ORDER' ? '주문' : '견적문의'}
              </span>
              <span className="order-alert-body">
                <b>{a.customer_name}</b> · {a.title || a.shape || a.order_no}
                <small>
                  {a.order_no} · {new Date(a.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                </small>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// 사용자 화면(홈 등)에서 관리자로 로그인된 상태일 때만 신규 주문 알림을 띄운다
export function SiteAdminOrderAlert() {
  const [session] = useAdminSession();
  const { alerts, dismissAlert } = useAdminOrderNotifications(session?.token);

  if (!session) return null;
  return <AdminOrderAlertPopup alerts={alerts} onDismiss={dismissAlert} className="is-site" />;
}
